import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { getContext } from 'recompose'
import pure from 'recompose/pure'

import Pagination from '@/ui/Pagination/Pagination'
import PaginationItem from '@/ui/Pagination/PaginationItem'
import PaginationLink from '@/ui/Pagination/PaginationLink'
import IconLeft from '@/icons/TriangleLeft'
import IconRight from '@/icons/TriangleRight'
import translate from '@/i18n/translate'

export class Paginator extends Component {
  static propTypes = {
    theme: PropTypes.object,
    page: PropTypes.number,
    perPage: PropTypes.number,
    total: PropTypes.number,
    setPage: PropTypes.func,
    showRange: PropTypes.bool, // Shows info from record x of total
    nbLabels: PropTypes.bool, // Shows Next/Prev labels beside arrows
    translate: PropTypes.func.isRequired,
  }

  getNbPages() {
    return Math.ceil(this.props.total / this.props.perPage) || 1
  }

  range() {
    const input = []
    const { page, perPage, total } = this.props
    if (isNaN(page)) return input
    const nbPages = Math.ceil(total / perPage) || 1

    // display page links around the current page
    if (page > 2) {
      input.push('1')
    }
    if (page === 4) {
      input.push('2')
    }
    if (page > 4) {
      input.push('.')
    }
    if (page > 1) {
      input.push(page - 1)
    }
    input.push(page)
    if (page < nbPages) {
      input.push(page + 1)
    }
    if (page === nbPages - 3) {
      input.push(nbPages - 1)
    }
    if (page < nbPages - 3) {
      input.push('.')
    }
    if (page < nbPages - 1) {
      input.push(nbPages)
    }

    return input
  }

  prevPage = event => {
    event.stopPropagation()
    if (this.props.page === 1) {
      throw new Error(this.props.translate('i18n.navigation.page_out_from_begin'))
    }
    this.props.setPage(this.props.page - 1)
  }

  nextPage = event => {
    event.stopPropagation()
    if (this.props.page > this.getNbPages()) {
      throw new Error(this.props.translate('i18n.navigation.page_out_from_end'))
    }
    this.props.setPage(this.props.page + 1)
  }

  gotoPage = event => {
    event.stopPropagation()
    const page = event.currentTarget.dataset.page
    if (page < 1 || page > this.getNbPages()) {
      throw new Error(this.props.translate('i18n.navigation.page_out_of_boundaries', { page }))
    }
    this.props.setPage(page)
  }

  renderPageNums() {
    return this.range().map((pageNum, index) => (
      <Choose>
        <When condition={pageNum === '.'}>
          <span
            key={`hyphen_${index}`}
            style={{ padding: '0 1.2em', fontWeight: 900, lineHeight: '2em' }}
          >
            &hellip;
          </span>
        </When>
        <Otherwise>
          <PaginationItem
            key={pageNum}
            tag="div"
            theme={this.props.theme}
            {...pageNum === this.props.page && { active: true }}
          >
            <PaginationLink
              tag="span"
              theme={this.props.theme}
              data-page={pageNum}
              onClick={this.gotoPage}
            >
              {pageNum}
            </PaginationLink>
          </PaginationItem>
        </Otherwise>
      </Choose>
    ))
  }

  render() {
    const { page, perPage, total, nbLabels, showRange, translate, theme } = this.props
    if (total === 0) return null
    const offsetEnd = Math.min(page * perPage, total)
    const offsetBegin = Math.min((page - 1) * perPage + 1, offsetEnd)
    const nbPages = this.getNbPages()

    return (
      <div className={theme['flex-center']}>
        <If condition={showRange}>
          <div>
            <span style={{ padding: '1.2em' }}>
              {translate('i18n.navigation.page_range_info', { offsetBegin, offsetEnd, total })}
            </span>
          </div>
        </If>
        {nbPages > 1 && (
          <Pagination tag="div" theme={theme}>
            <PaginationItem tag="div" key="prev" theme={theme} disabled={page === 1}>
              <PaginationLink tag="span" theme={theme} onClick={this.prevPage}>
                <IconLeft />
                <If condition={nbLabels}>{translate('i18n.navigation.prev')}</If>
              </PaginationLink>
            </PaginationItem>
            {this.renderPageNums()}
            <PaginationItem tag="div" key="next" theme={theme} disabled={page === nbPages}>
              <PaginationLink tag="span" theme={theme} onClick={this.nextPage}>
                <If condition={nbLabels}>{translate('i18n.navigation.next')}</If>
                <IconRight />
              </PaginationLink>
            </PaginationItem>
          </Pagination>
        )}
      </div>
    )
  }
}
const withTheme = getContext({ theme: PropTypes.object })

const dataPaginator = translate(pure(Paginator))
const themedPaginator = withTheme(dataPaginator)

export default dataPaginator
export { themedPaginator as dataPaginator }



// WEBPACK FOOTER //
// ./ui/DataTable/Pagination.js